/**
 * Local UI preview harness — DEV ONLY.
 *
 * The authed portal needs Firebase auth + a live DB, neither of which runs
 * locally. This harness lets the real portal components render against mock
 * data so the design can be reviewed and iterated offline.
 *
 * Activate by adding `?preview=1` to any URL (persists for the tab via
 * sessionStorage). Deactivate with `?preview=0`. It is hard-gated to
 * `import.meta.env.DEV`, so it can never activate in a production build.
 *
 * How it works:
 *  - `useAuth` returns PREVIEW_USER when active (fakes a signed-in instructor).
 *  - `installPreviewFetch()` monkey-patches `window.fetch` to answer `/api/*`
 *    reads (and swallow writes) from the fixtures below. One interception
 *    point covers react-query's default fetcher, components that call `fetch`
 *    directly (e.g. PanelProfile), and mutations.
 */

import type { User } from "firebase/auth";

export function isPreview(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const flag = params.get("preview");
  if (flag === "1") sessionStorage.setItem("tb-preview", "1");
  else if (flag === "0") sessionStorage.removeItem("tb-preview");
  return sessionStorage.getItem("tb-preview") === "1";
}

export const PREVIEW_USER = {
  uid: "preview-uid",
  email: "preview@trainingbooker.com",
  displayName: "Sarah Mitchell",
  emailVerified: true,
  getIdToken: async () => "preview-token",
} as unknown as User;

// ── Fixtures ────────────────────────────────────────────────────────────────

const PREVIEW_DB_USER = {
  id: "preview-uid",
  uid: "preview-uid",
  firebaseUid: "preview-uid",
  email: "preview@trainingbooker.com",
  firstName: "Sarah",
  lastName: "Mitchell",
  onboardingCompleted: true,
  isAdmin: true, // admins bypass the paywall check in the portal guard
  subscriptionStatus: "active",
  planType: "pro",
};

const PREVIEW_PROFILE = {
  id: "preview-profile-1",
  slug: "sarah-mitchell",
  publicId: "ab12cd",
  profileStatus: "live",
  publishPaid: true,
  claimedByUserId: "preview-uid",
  firstName: "Sarah",
  lastName: "Mitchell",
  headshotUrl: "",
  coverImageUrl: "",
  headline: "Calm, patient driving lessons for nervous first-timers",
  jobTitle: "Licensed Driving Instructor (ADI)",
  primaryExpertise: "Driving Instruction",
  longBio:
    "I help nervous first-timers and returning drivers pass with confidence. After fifteen years and 800+ passes, my whole approach is calm, judgement-free, and paced to you. No shouting, no dual-control drama, just steady progress lesson to lesson.",
  expertiseTags: ["Nervous drivers", "Pass Plus", "Motorway confidence", "Manual & automatic"],
  locationCity: "North London",
  locationRegion: "London",
  orgName: "Mitchell Driving School",
  credentials: [
    { name: "DVSA Approved Driving Instructor (ADI)", issuer: "DVSA" },
    { name: "Pass Plus Registered", issuer: "DVSA" },
  ],
  bookingUrl: "https://calendly.com/sarah-driving",
  websiteUrl: "https://sarahdriving.example.com",
  contactEmailPublic: "hello@sarahdriving.example.com",
  socialLinksJson: [
    { platform: "Instagram", url: "https://instagram.com/sarahdrives", order: 0 },
  ],
};

const PREVIEW_LISTINGS = {
  opportunities: [
    {
      id: "op1",
      title: "Intro Assessment Lesson",
      startDatetime: "2026-07-28T09:00:00",
      pricePerPerson: 45,
      locationLabel: "North London",
      bookingUrl: "https://calendly.com/sarah-driving/intro",
      status: "active",
    },
    {
      id: "op2",
      title: "90-min Standard Lesson",
      startDatetime: "2026-07-29T14:00:00",
      pricePerPerson: 65,
      locationLabel: "North London",
      bookingUrl: "https://calendly.com/sarah-driving/standard",
      status: "active",
    },
    {
      id: "op3",
      title: "Motorway Confidence Session",
      startDatetime: "2026-07-30T10:30:00",
      pricePerPerson: 80,
      locationLabel: "North London",
      bookingUrl: "https://calendly.com/sarah-driving/motorway",
      status: "active",
    },
  ],
};

/** Resolve a fixture for a given API path, or `undefined` to fall through. */
function fixtureFor(path: string, method: string): unknown | undefined {
  // Writes: swallow so preview clicks don't throw. Return the profile for
  // profile PATCHes so optimistic reads stay coherent.
  if (method !== "GET") {
    if (path.startsWith("/api/profiles/")) return PREVIEW_PROFILE;
    if (path.startsWith("/api/billing/publish-checkout")) return { alreadyPaid: true };
    if (path.startsWith("/api/")) return { ok: true };
    return undefined;
  }
  if (path.startsWith("/api/user")) return PREVIEW_DB_USER;
  if (path.startsWith("/api/profiles/by-user/")) return PREVIEW_PROFILE;
  if (path.startsWith("/api/profiles/by-slug/")) return PREVIEW_PROFILE;
  if (path.startsWith("/api/learning-opportunities/search")) return PREVIEW_LISTINGS;
  if (path.startsWith("/api/leads")) return [];
  if (path.startsWith("/api/reviews/instructor/")) return [];
  if (path.startsWith("/api/review-invitations/instructor/")) return [];
  if (path.startsWith("/api/learner/profile")) return null;
  return undefined;
}

let installed = false;

export function installPreviewFetch(): void {
  if (installed || !isPreview()) return;
  installed = true;
  const realFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;
      const path = url.replace(window.location.origin, "");
      const method = (init?.method || (input as Request)?.method || "GET").toUpperCase();
      if (path.startsWith("/api/")) {
        const fixture = fixtureFor(path, method);
        if (fixture !== undefined) {
          // eslint-disable-next-line no-console
          console.log(`[preview:fetch] ${method} ${path} -> fixture`);
          return new Response(JSON.stringify(fixture), {
            status: 200,
            headers: { "Content-Type": "application/json" },
          });
        }
      }
    } catch {
      /* fall through to the real fetch on any shim error */
    }
    return realFetch(input, init);
  };
  // eslint-disable-next-line no-console
  console.log("[preview] Local preview mode ACTIVE (mock auth + API fixtures).");
}
